/**
 * DopeOS UI System - Terminal Green Design System
 *
 * Centralized UI resources for all views.
 * Terminal green only - no other colors for now.
 *
 * @module engine/ui/DopeOS-UI
 */

import { DopeTextRenderer } from '../renderer/bitmapFont';
import { interactiveRegistry } from '../interactivity';

// ============================================================
// TERMINAL COLOR PALETTE
// ============================================================

export const TERMINAL = {
  // Primary greens
  GREEN: '#0d4f0d',        // Main DOPE green
  GREEN_LIGHT: '#1a6f1a',  // Hover states / highlights
  GREEN_DARK: '#051405',   // Background fills
  GREEN_DARKEST: '#020a02', // Empty states

  // Text
  TEXT: '#39FF14',         // Bright terminal text
  TEXT_DIM: '#1a5f1a',     // Muted text
  TEXT_BRIGHT: '#5fff5f',  // Active / selected text

  // Utility
  BLACK: '#000000',
  WHITE: '#ffffff',

  // State colors
  HOVER: '#1a5f1a',
  ACTIVE: '#2a8f2a',
  DISABLED: '#0a2f0a'
};

// ============================================================
// SPACING SYSTEM
// ============================================================

export const SPACING = {
  XS: 4,
  SM: 8,
  MD: 16,
  LG: 24,
  XL: 32
};

// ============================================================
// PROGRESS BAR (Dithered Bayer - Clean)
// ============================================================

const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5]
].map(row => row.map(v => v / 16));

/**
 * Draw terminal progress bar with clean Bayer dither
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number} progress - 0.0 to 1.0
 * @param {Object} opts
 */
export function ProgressBar(ctx, x, y, width, height, progress, opts = {}) {
  const {
    label = null,
    showPercent = false,
    pixelSize = 3
  } = opts;

  progress = Math.max(0, Math.min(1, progress));

  // Background (darkest green)
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(x, y, width, height);

  // Border
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, height);

  // Fill width
  const fillWidth = Math.floor(width * progress);
  if (fillWidth <= 0) return;

  // Dithered fill using Bayer matrix
  ctx.fillStyle = TERMINAL.GREEN;
  const cols = Math.ceil(fillWidth / pixelSize);
  const rows = Math.ceil(height / pixelSize);

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const threshold = BAYER_4X4[row % 4][col % 4];
      const pixelProgress = col / cols;

      // Solid for main fill, dither for transition
      if (pixelProgress < progress - 0.1) {
        ctx.fillRect(
          x + col * pixelSize,
          y + row * pixelSize,
          pixelSize,
          pixelSize
        );
      } else if (pixelProgress < progress + 0.05 && threshold < 0.5) {
        ctx.fillRect(
          x + col * pixelSize,
          y + row * pixelSize,
          pixelSize,
          pixelSize
        );
      }
    }
  }

  // Label
  if (label) {
    ctx.fillStyle = TERMINAL.TEXT;
    const metrics = DopeTextRenderer.measureDOSText(label, 1);
    DopeTextRenderer.renderDOSText(ctx, label, x - 8 - metrics.width, y + (height - metrics.height) / 2, 1);
  }

  // Percentage
  if (showPercent) {
    ctx.fillStyle = progress > 0.5 ? TERMINAL.GREEN_DARKEST : TERMINAL.TEXT;
    const value = `${Math.floor(progress * 100)}%`;
    const metrics = DopeTextRenderer.measureDOSText(value, 1);
    DopeTextRenderer.renderDOSText(ctx, value, x + (width - metrics.width) / 2, y + (height - metrics.height) / 2, 1);
  }
}

// ============================================================
// HEALTH/RESOURCE BAR
// ============================================================

/**
 * Draw health or resource bar
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number} current
 * @param {number} max
 * @param {Object} opts
 */
export function ResourceBar(ctx, x, y, width, height, current, max, opts = {}) {
  const {
    label = 'HP',
    warningThreshold = 0.25
  } = opts;

  const progress = Math.max(0, Math.min(1, current / max));
  const isLow = progress <= warningThreshold;

  // Use brighter color when low (warning state)
  const fillColor = isLow ? TERMINAL.TEXT : TERMINAL.GREEN;
  const textColor = isLow ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;

  // Background
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(x, y, width, height);

  // Border (brighter when low)
  ctx.strokeStyle = isLow ? TERMINAL.TEXT : TERMINAL.GREEN;
  ctx.lineWidth = isLow ? 2 : 1;
  ctx.strokeRect(x, y, width, height);

  // Fill
  const fillWidth = Math.floor(width * progress);
  if (fillWidth > 0) {
    ctx.fillStyle = fillColor;

    // Simple dither for low health (more visible)
    const pixelSize = isLow ? 2 : 3;
    const cols = Math.ceil(fillWidth / pixelSize);
    const rows = Math.ceil(height / pixelSize);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const threshold = BAYER_4X4[row % 4][col % 4];
        const pixelProgress = col / cols;

        if (pixelProgress < progress - 0.1 ||
            (pixelProgress < progress + 0.05 && threshold < 0.5)) {
          ctx.fillRect(
            x + col * pixelSize,
            y + row * pixelSize,
            pixelSize,
            pixelSize
          );
        }
      }
    }
  }

  // Label
  ctx.fillStyle = textColor;
  const labelMetrics = DopeTextRenderer.measureDOSText(label, 1);
  DopeTextRenderer.renderDOSText(ctx, label, x - 8 - labelMetrics.width, y + (height - labelMetrics.height) / 2, 1);

  // Value
  ctx.fillStyle = isLow ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  const value = `${Math.floor(current)}/${max}`;
  const valueMetrics = DopeTextRenderer.measureDOSText(value, 1);
  DopeTextRenderer.renderDOSText(ctx, value, x + (width - valueMetrics.width) / 2, y + (height - valueMetrics.height) / 2, 1);
}

// ============================================================
// BUTTON
// ============================================================

/**
 * Draw terminal button
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {string} text
 * @param {string} state - 'normal' | 'hover' | 'pressed' | 'disabled'
 */
export function Button(ctx, x, y, width, height, text, state = 'normal') {
  const colors = {
    normal: { bg: TERMINAL.GREEN_DARK, border: TERMINAL.GREEN, text: TERMINAL.TEXT },
    hover: { bg: TERMINAL.GREEN, border: TERMINAL.TEXT, text: TERMINAL.GREEN_DARKEST },
    pressed: { bg: TERMINAL.GREEN_DARKEST, border: TERMINAL.GREEN, text: TERMINAL.TEXT_DIM },
    disabled: { bg: TERMINAL.GREEN_DARKEST, border: TERMINAL.GREEN_DARK, text: TERMINAL.TEXT_DIM }
  };

  const c = colors[state] || colors.normal;

  // Background
  ctx.fillStyle = c.bg;
  ctx.fillRect(x, y, width, height);

  // Border
  ctx.strokeStyle = c.border;
  ctx.lineWidth = state === 'hover' ? 2 : 1;
  ctx.strokeRect(x, y, width, height);

  // 3D effect (highlight top, shadow bottom)
  if (state !== 'pressed') {
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(x + 1, y + 1, width - 2, 2);
  } else {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(x + 1, y + height - 3, width - 2, 2);
  }

  // Text
  ctx.fillStyle = c.text;
  const metrics = DopeTextRenderer.measureDOSText(text, 1);
  DopeTextRenderer.renderDOSText(
    ctx,
    text,
    x + (width - metrics.width) / 2,
    y + (height - metrics.height) / 2,
    1
  );
}

/**
 * Register button interaction
 *
 * @param {string} id
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {Function} onClick
 * @returns {Object} Button state tracker
 */
export function registerButton(id, x, y, width, height, onClick) {
  const buttonState = { state: 'normal' };

  interactiveRegistry.register({
    id,
    x,
    y,
    width,
    height,
    action: 'callback',
    callback: onClick
  });

  return buttonState;
}

// ============================================================
// PANEL / WINDOW
// ============================================================

/**
 * Draw terminal panel with border
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {string} title - Optional title
 */
export function Panel(ctx, x, y, width, height, title = null) {
  // Background
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(x, y, width, height);

  // Border
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, height);

  // Title bar
  if (title) {
    ctx.fillStyle = TERMINAL.GREEN_DARK;
    ctx.fillRect(x, y, width, 20);

    ctx.strokeStyle = TERMINAL.GREEN;
    ctx.beginPath();
    ctx.moveTo(x, y + 20);
    ctx.lineTo(x + width, y + 20);
    ctx.stroke();

    ctx.fillStyle = TERMINAL.TEXT;
    DopeTextRenderer.renderDOSText(ctx, `[ ${title} ]`, x + 8, y + 2, 1);
  }
}

// ============================================================
// TEXT
// ============================================================

/**
 * Draw terminal text with proper styling
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {string} style - 'normal' | 'dim' | 'bright' | 'header'
 * @param {number} scale
 */
export function Text(ctx, text, x, y, style = 'normal', scale = 1) {
  const colors = {
    normal: TERMINAL.TEXT,
    dim: TERMINAL.TEXT_DIM,
    bright: TERMINAL.TEXT_BRIGHT,
    header: TERMINAL.TEXT_BRIGHT
  };

  ctx.fillStyle = colors[style] || TERMINAL.TEXT;
  DopeTextRenderer.renderDOSText(ctx, text, x, y, scale);
}

/**
 * Draw header/title text
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {number} scale
 */
export function Header(ctx, text, x, y, scale = 1.5) {
  Text(ctx, text, x, y, 'header', scale);
}

// ============================================================
// SCROLLBAR
// ============================================================

/**
 * Draw terminal scrollbar
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number} progress - 0.0 to 1.0
 * @param {number} thumbSize - 0.0 to 1.0
 */
export function Scrollbar(ctx, x, y, width, height, progress, thumbSize = 0.2) {
  // Track
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(x, y, width, height);

  ctx.strokeStyle = TERMINAL.GREEN_DARK;
  ctx.strokeRect(x, y, width, height);

  // Thumb
  const thumbHeight = Math.max(20, height * thumbSize);
  const maxTravel = height - thumbHeight;
  const thumbY = y + progress * maxTravel;

  ctx.fillStyle = TERMINAL.GREEN;
  ctx.fillRect(x + 1, thumbY, width - 2, thumbHeight);

  // Thumb detail (lines)
  ctx.fillStyle = TERMINAL.GREEN_DARK;
  const lineY = thumbY + thumbHeight / 2 - 2;
  ctx.fillRect(x + 3, lineY, width - 6, 1);
  ctx.fillRect(x + 3, lineY + 3, width - 6, 1);
}

// ============================================================
// SPINNER / LOADING
// ============================================================

/**
 * Draw loading spinner
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} size
 * @param {number} time
 */
export function Spinner(ctx, x, y, size, time) {
  const steps = 8;
  const step = Math.floor((time * 8) % steps);

  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * Math.PI * 2 - Math.PI / 2;
    const dist = size / 2;
    const px = Math.round(x + Math.cos(angle) * dist);
    const py = Math.round(y + Math.sin(angle) * dist);

    const isActive = i === step;
    const isNext = ((i + 1) % steps) === step;

    ctx.fillStyle = isActive ? TERMINAL.TEXT_BRIGHT :
                    isNext ? TERMINAL.TEXT : TERMINAL.GREEN_DARK;

    const dotSize = isActive ? 4 : 2;
    ctx.fillRect(px - dotSize / 2, py - dotSize / 2, dotSize, dotSize);
  }
}

// ============================================================
// GRID / LAYOUT HELPERS
// ============================================================

/**
 * Create a grid layout helper
 *
 * @param {number} x - Grid origin X
 * @param {number} y - Grid origin Y
 * @param {number} cols - Number of columns
 * @param {number} rows - Number of rows
 * @param {number} cellWidth - Width of each cell
 * @param {number} cellHeight - Height of each cell
 * @param {number} gap - Gap between cells
 */
export function Grid(x, y, cols, rows, cellWidth, cellHeight, gap = 8) {
  return {
    cell: (col, row) => ({
      x: x + col * (cellWidth + gap),
      y: y + row * (cellHeight + gap),
      width: cellWidth,
      height: cellHeight
    }),
    center: (col, row) => ({
      x: x + col * (cellWidth + gap) + cellWidth / 2,
      y: y + row * (cellHeight + gap) + cellHeight / 2
    })
  };
}

// ============================================================
// EXPORTS
// ============================================================

export const DopeUI = {
  // Colors
  TERMINAL,
  SPACING,

  // Components
  ProgressBar,
  ResourceBar,
  Button,
  Panel,
  Text,
  Header,
  Scrollbar,
  Spinner,

  // Helpers
  registerButton,
  Grid
};

export default DopeUI;
